import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const services = [
  {
    title: 'Bathroom Remodeling',
    description: 'From simple updates to complete transformations, we create beautiful bathrooms for every budget.',
    image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&q=80&w=800',
    path: '/services/bathroom-remodeling-westchester',
    gradient: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    title: 'Kitchen Renovation',
    description: 'Quality kitchen renovations tailored to your needs, whether refreshing or fully remodeling.',
    image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80&w=800',
    path: '/services/kitchen-renovation-westchester',
    gradient: 'from-orange-500/20 to-red-500/20'
  },
  {
    title: 'Home Additions',
    description: "Thoughtfully designed additions that complement your home, from simple expansions to major extensions.",
    image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&q=80&w=800',
    path: '/services/home-additions-westchester',
    gradient: 'from-green-500/20 to-emerald-500/20'
  }
];

export function Services() {
  return (
    <section id="services" className="py-24 md:py-32">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #fbbf24 2px, transparent 2px),
                           radial-gradient(circle at 75% 75%, #fbbf24 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16 md:mb-24"
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-yellow-400" />
            <h2 className="section-subheading">Services</h2>
          </div>
          <h3 className="section-heading relative">
            Home Services for Every Need
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "200px" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-yellow-400 to-transparent"
            />
          </h3>
          <p className="text-zinc-400 mt-6 max-w-2xl">
            From simple updates to complete renovations, we serve homeowners throughout Westchester County 
            and Lower Connecticut with the same dedication to quality craftsmanship, regardless of project size.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group"
            >
              <Link 
                to={service.path}
                className="relative bg-zinc-800/30 rounded-xl overflow-hidden block h-full transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-400/10"
              >
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    loading="eager"
                    width="800"
                    height="600"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 group-hover:brightness-110"
                  />
                </div>
                <div className="p-6 relative z-10">
                  <h4 className="text-xl font-light mb-3 text-zinc-100">{service.title}</h4>
                  <p className="text-zinc-400 mb-4">{service.description}</p>
                  <div className="flex items-center gap-2 text-yellow-400 group-hover:gap-4 transition-all duration-300">
                    <span className="text-sm tracking-wider">Learn More</span>
                    <ArrowRight className="h-4 w-4 group-hover:rotate-12 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link 
              to="/services"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900 group transition-all duration-300 relative overflow-hidden shadow-lg hover:shadow-xl"
            >
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="text-sm tracking-wider relative z-10">View All Services</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 relative z-10" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}